import { Field, useFormikContext } from "formik"
import { useEffect } from "react"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"

import Error from "@/components/Error/Error"
import RequiredInd from "@/components/RequiredInd/RequiredInd"
import type { Option } from "@/types"

import { useTranslation } from "react-i18next"
import styles from "./Inputs.module.css"

interface SelectFieldProps {
  options: Option[]
  name: string
  error: React.ReactNode
  backError?: string | number
  className: string
  placeholder: string
  title: string
  id: string
  row?: boolean
  required?: boolean
  isMulti?: boolean
  isClearable?: boolean
  disabled?: boolean
  disabledLabel?: string
  extraFunOnChange?: (value: unknown) => void
  clear?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customComponents?: any
  style?: React.CSSProperties
  onChange?: (value: unknown) => void
  isCreatable?: boolean
  onCreateOption?: (option: Option) => void
}
export default function SelectField({
  options,
  name,
  error,
  backError,
  className,
  row,
  placeholder,
  title,
  id,
  required,
  isMulti,
  isClearable,
  disabled,
  disabledLabel,
  extraFunOnChange,
  clear,
  customComponents,
  style,
  onChange,
  isCreatable, // <-- New prop to enable custom input
  onCreateOption, // <-- Optional callback when new option is created
}: SelectFieldProps) {
  const { setFieldValue } = useFormikContext()
  const { t } = useTranslation()
  return (
    <div
      style={style}
      className={`d-flex w-100 flex-column ${
        row ? "flex-md-row align-items-md-center gap-3" : "flex-column gap-2 "
      } ${className ? className : ""}`}
    >
      <div className="d-flex gap-2 align-items-center justify-content-between">
        <label htmlFor={id} className="modal__label fw-bold">
          {title} {row ? ":" : ""}
        </label>
        {required ? <RequiredInd /> : ""}
        {clear && (
          <button
            type="button"
            className={styles["btn-clear"]}
            onClick={() => setFieldValue(name, "")}
          >
            {t("buttons.clear")}
          </button>
        )}
      </div>
      <div className="w-100 h-100">
        <Field
          name={name}
          id={name}
          component={SelectInp}
          options={options}
          className={`${styles.select}`}
          placeholder={placeholder}
          isMulti={isMulti}
          isClearable={isClearable}
          disabled={disabled}
          disabledLabel={disabledLabel}
          extraFunOnChange={extraFunOnChange}
          customComponents={customComponents}
          onChange={onChange}
          isCreatable={isCreatable}
          onCreateOption={onCreateOption}
        />

        {<Error className={"mt-3"}>{backError || error}</Error>}
      </div>
    </div>
  )
}

const SelectInp = ({
  field,
  form,
  options,
  className,
  placeholder = "Select",
  isMulti,
  isClearable,
  disabled,
  disabledLabel,
  extraFunOnChange,
  customComponents,
  onChange,
  isCreatable, // <-- Receive the isCreatable prop
  onCreateOption, // <-- Receive the onCreateOption callback
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: SelectFieldProps & { field: any; form: any }) => {
  const SelectComponent = isCreatable ? CreatableSelect : Select

  useEffect(() => {
    if (extraFunOnChange) {
      extraFunOnChange(field.value)
    }
  }, [field.value])

  return (
    <SelectComponent
      options={options}
      name={field.name}
      value={
        isMulti
          ? options.filter((option) => field.value?.includes(option.value))
          : options.find((option) => option.value === field.value) ||
            (field.value ? { label: field.value, value: field.value } : "")
      }
      className={className}
      onChange={(selectedOptions) => {
        if (isMulti) {
          if (Array.isArray(selectedOptions)) {
            const selectedValues = selectedOptions
              ? selectedOptions.map((option) => option.value)
              : []
            form.setFieldValue(field.name, selectedValues)
          }
        } else if (
          selectedOptions &&
          typeof selectedOptions === "object" &&
          "value" in selectedOptions
        ) {
          form.setFieldValue(
            field.name,
            selectedOptions ? selectedOptions.value : ""
          )
        }

        if (onChange) {
          onChange(selectedOptions)
        }
      }}
      onCreateOption={
        isCreatable
          ? (inputValue) => {
              const newOption = { label: inputValue, value: inputValue }

              if (isMulti) {
                const currentValues = field.value || []
                form.setFieldValue(field.name, [...currentValues, inputValue])
              } else {
                form.setFieldValue(field.name, inputValue)
              }

              if (onCreateOption) {
                onCreateOption(newOption)
              }

              if (onChange) {
                onChange(
                  isMulti ? [...(field.value || []), newOption] : newOption
                )
              }
            }
          : undefined
      }
      onBlur={field.onBlur}
      placeholder={disabled ? disabledLabel || placeholder : placeholder}
      isMulti={isMulti}
      isClearable={isClearable}
      isDisabled={disabled}
      formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
      styles={{
        container: (base) => ({
          ...base,
          border: "none",
          backgroundColor: "transparent",
          height: "fit-content",
        }),
        control: (base) => ({
          ...base,
          border: " 1px solid rgba(0, 0, 0, 0.1)",
          outline: "none",
          boxShadow: "none",
          borderRadius: "12px",

          "&:hover": {
            border: "1px solid rgba(0, 0, 0, 0.1)",
          },
        }),
        placeholder: (base) => ({ ...base, color: "#8F8F8F" }),
        valueContainer: (base) => ({
          ...base,
          overflowX: "auto",
          flexWrap: "unset",
          padding: "12px 24px",
        }),
        multiValue: (base) => ({
          ...base,
          minWidth: "fit-content",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          paddingRight: "24px",
          color: "black",
        }),
        indicatorSeparator: (base) => ({ ...base, display: "none" }),
        menuPortal: (base) => ({ ...base, zIndex: 999999999999999 }),
      }}
      components={customComponents}
    />
  )
}
